import { isPlainObject } from 'lodash';
import { ServiceArea } from '../../types/serviceArea';
import { CenterOnTarget } from '../service_area_map/ServiceAreaMap';
import { COUNTY_EQUIVALENT_PLACE_TYPES, PLACE_BUCKET_EQUIVALENT_TYPES } from './constants';

export const getNameParts = (name: string): string[] =>
	(name || '')
		.split(',')
		.map((_) => _.trim())
		.filter((_) => !!_);

/**
 * Chip / list label: backend or geocoder sometimes stringifies missing parts as "null".
 * Replace those comma-separated tokens with "-" for display only.
 */
export function formatServiceAreaChipLabel(name: string | undefined | null): string {
	if (name === undefined || name === null) {
		return '';
	}
	const raw = String(name);
	if (raw.length === 0) {
		return '';
	}
	const segments = raw.split(',');
	const replaced: string[] = [];
	for (let segmentIndex = 0; segmentIndex < segments.length; segmentIndex++) {
		const trimmed = segments[segmentIndex].trim();
		if (trimmed.toLowerCase() === 'null') {
			replaced.push('-');
		} else {
			replaced.push(trimmed);
		}
	}
	return replaced.join(', ');
}

export const getCountryFromArea = (area: ServiceArea): string => {
	if (area.placeType === 'poi') {
		if (area.pinLabel) {
			const parts = getNameParts(area.pinLabel);
			return parts.length > 0 ? parts[parts.length - 1] : 'Precise coordinates';
		}
		return 'Precise coordinates';
	}
	const parts = getNameParts(area.name);
	if (area.placeType === 'country') return parts[0] || area.name;
	return parts.length > 0 ? parts[parts.length - 1] : '';
};

export const getStateFromArea = (area: ServiceArea): string => {
	if (area.placeType === 'poi') {
		if (area.pinLabel) {
			const parts = getNameParts(area.pinLabel);
			return parts.length >= 2 ? parts[parts.length - 2] : '';
		}
		return '';
	}
	const parts = getNameParts(area.name);
	if (area.placeType === 'region') {
		return parts[0] || '';
	}
	if (area.placeType === 'district') {
		return parts.length >= 2 ? parts[parts.length - 2] : '';
	}
	if (area.placeType === 'place') {
		return parts.length >= 2 ? parts[parts.length - 2] : '';
	}
	return '';
};

export const normalizePlaceTypeForMode = (placeType: string, mode: string): string => {
	if (mode === 'place') {
		if (PLACE_BUCKET_EQUIVALENT_TYPES.has(placeType)) {
			return 'place';
		}
		if (placeType === 'postcode') {
			return 'place';
		}
		if (placeType === 'district') {
			return 'place';
		}
		return placeType;
	}
	if (placeType === 'postcode') {
		return 'district';
	}
	if (mode === 'district' && COUNTY_EQUIVALENT_PLACE_TYPES.has(placeType)) {
		return 'district';
	}
	return placeType;
};

/**
 * Intentionally does NOT read `boundaries` from the response: the backend no longer persists
 * geometry, and the CDN is the sole source. `EditSupplierServiceAreasForm` / `ServiceAreasCard`
 * hydrate `boundaries` via `resolveServiceAreaBoundariesFromCDN` after this parser runs, using
 * `boundaryShapeId` + `boundaryShapeGroup` + `placeType` to derive the `{ISO}-{ADM}.geojson` URL.
 */
function serviceAreaItemToServiceArea(
	serviceAreaItem: any,
	defaultPlaceType: string
): ServiceArea | null {
	if (!serviceAreaItem || typeof serviceAreaItem !== 'object') {
		return null;
	}
	const center =
		serviceAreaItem.center && serviceAreaItem.center.length === 2
			? ([serviceAreaItem.center[0], serviceAreaItem.center[1]] as [number, number])
			: ([0, 0] as [number, number]);
	const bbox =
		serviceAreaItem.bbox && serviceAreaItem.bbox.length === 4
			? ([
					serviceAreaItem.bbox[0],
					serviceAreaItem.bbox[1],
					serviceAreaItem.bbox[2],
					serviceAreaItem.bbox[3],
			  ] as [number, number, number, number])
			: null;
	const pinLabelRaw = serviceAreaItem.pinLabel;
	return {
		name: String(serviceAreaItem.name || ''),
		placeType: serviceAreaItem.placeType || defaultPlaceType,
		center: center,
		bbox: bbox,
		all: serviceAreaItem.all === true,
		pinLabel:
			pinLabelRaw !== undefined && pinLabelRaw !== null && String(pinLabelRaw).length > 0
				? String(pinLabelRaw)
				: undefined,
		boundaryShapeId: serviceAreaItem.boundaryShapeId || undefined,
		boundaryShapeGroup: serviceAreaItem.boundaryShapeGroup || undefined,
		parentStateShapeId:
			serviceAreaItem.parentStateShapeId != null
				? String(serviceAreaItem.parentStateShapeId)
				: null,
		parentCountryShapeId:
			serviceAreaItem.parentCountryShapeId != null
				? String(serviceAreaItem.parentCountryShapeId)
				: null,
	};
}

/**
 * Legacy bucketed read path: `{ countries, states, counties, places, pins }`.
 * Kept only for facilities that haven't been re-saved since the flat-array migration.
 */
export const areasFromHierarchy = (serviceAreasHierarchy: any): ServiceArea[] => {
	if (!serviceAreasHierarchy || typeof serviceAreasHierarchy !== 'object') {
		return [];
	}
	const bucketNames = ['countries', 'states', 'counties', 'places', 'pins'];
	const flattenedAreas: ServiceArea[] = [];
	for (let bucketIndex = 0; bucketIndex < bucketNames.length; bucketIndex++) {
		const bucketKey = bucketNames[bucketIndex];
		const bucketItems = serviceAreasHierarchy[bucketKey];
		if (!Array.isArray(bucketItems)) {
			continue;
		}
		const defaultPlaceType =
			bucketKey === 'pins'
				? 'poi'
				: bucketKey === 'places'
				? 'place'
				: bucketKey === 'countries'
				? 'country'
				: bucketKey === 'states'
				? 'region'
				: 'district';
		for (let itemIndex = 0; itemIndex < bucketItems.length; itemIndex++) {
			const converted = serviceAreaItemToServiceArea(bucketItems[itemIndex], defaultPlaceType);
			if (converted) {
				flattenedAreas.push(converted);
			}
		}
	}
	return flattenedAreas;
};

export const areasFromFlatArray = (serviceAreasArray: any): ServiceArea[] => {
	if (!Array.isArray(serviceAreasArray)) {
		return [];
	}
	const flattenedAreas: ServiceArea[] = [];
	for (let itemIndex = 0; itemIndex < serviceAreasArray.length; itemIndex++) {
		const item = serviceAreasArray[itemIndex];
		// Flat-array items carry their own `placeType`; skip anything without one rather than
		// silently defaulting (which would mislabel countries/districts as regions).
		if (!item || typeof item !== 'object' || !item.placeType) {
			continue;
		}
		const converted = serviceAreaItemToServiceArea(item, String(item.placeType));
		if (converted) {
			flattenedAreas.push(converted);
		}
	}
	return flattenedAreas;
};

/**
 * Maps a single item from the GET /service_areas response to the internal ServiceArea shape.
 * `displayName` becomes `name`; `center` and `bbox` start null and are hydrated from CDN.
 */
export function serviceAreaFromApiItem(item: any): ServiceArea | null {
	if (!item || typeof item !== 'object') return null;
	const placeType = String(item.placeType || '');
	if (!placeType) return null;
	return {
		name: String(item.displayName || ''),
		placeType,
		center: [0, 0] as [number, number],
		bbox: null,
		boundaryShapeId: item.boundaryShapeId || undefined,
		boundaryShapeGroup: item.boundaryShapeGroup || undefined,
		parentStateShapeId: item.parentStateShapeId != null ? String(item.parentStateShapeId) : null,
		parentCountryShapeId:
			item.parentCountryShapeId != null ? String(item.parentCountryShapeId) : null,
	};
}

export const admLevelFromPlaceType = (placeType: string): number => {
	if (placeType === 'country') return 0;
	if (placeType === 'region') return 1;
	return 2;
};

export const getInitialServiceAreasFromFacility = (facility: any): ServiceArea[] => {
	if (!facility) {
		return [];
	}

	const serviceAreasValue = facility.serviceAreas;
	if (Array.isArray(serviceAreasValue)) {
		return areasFromFlatArray(serviceAreasValue);
	}
	if (isPlainObject(serviceAreasValue)) {
		const areasFromBuckets = areasFromHierarchy(serviceAreasValue);
		if (areasFromBuckets.length > 0) {
			return areasFromBuckets;
		}
	}

	return [];
};

/** Default viewport shown when no service areas have been selected. */
export const USA_DEFAULT_CENTER_ON: CenterOnTarget = {
	center: [-98.58, 39.83] as [number, number],
	zoom: 3,
};

function bboxFromGeoJsonGeometry(geom: any): [number, number, number, number] | null {
	if (!geom || !geom.coordinates) return null;
	let minLng = Infinity;
	let minLat = Infinity;
	let maxLng = -Infinity;
	let maxLat = -Infinity;
	function walk(coords: any): void {
		if (!coords || !coords.length) return;
		if (typeof coords[0] === 'number') {
			if (coords[0] < minLng) minLng = coords[0];
			if (coords[0] > maxLng) maxLng = coords[0];
			if (coords[1] < minLat) minLat = coords[1];
			if (coords[1] > maxLat) maxLat = coords[1];
			return;
		}
		for (let i = 0; i < coords.length; i++) {
			walk(coords[i]);
		}
	}
	walk(geom.coordinates);
	if (minLng === Infinity || minLng > maxLng) return null;
	return [minLng, minLat, maxLng, maxLat];
}

export const getCenterOnTargetForAreas = (areas: ServiceArea[]): CenterOnTarget | null => {
	if (!areas || areas.length === 0) return null;
	let minLng = Infinity;
	let minLat = Infinity;
	let maxLng = -Infinity;
	let maxLat = -Infinity;
	let hasBbox = false;
	for (const area of areas) {
		const bbox = area.bbox;
		const isZeroBbox = bbox && bbox[0] === 0 && bbox[1] === 0 && bbox[2] === 0 && bbox[3] === 0;
		if (bbox && bbox.length === 4 && !isZeroBbox) {
			if (bbox[0] < minLng) minLng = bbox[0];
			if (bbox[1] < minLat) minLat = bbox[1];
			if (bbox[2] > maxLng) maxLng = bbox[2];
			if (bbox[3] > maxLat) maxLat = bbox[3];
			hasBbox = true;
		} else if (area.boundaries) {
			const geomBbox = bboxFromGeoJsonGeometry(area.boundaries);
			if (geomBbox) {
				if (geomBbox[0] < minLng) minLng = geomBbox[0];
				if (geomBbox[1] < minLat) minLat = geomBbox[1];
				if (geomBbox[2] > maxLng) maxLng = geomBbox[2];
				if (geomBbox[3] > maxLat) maxLat = geomBbox[3];
				hasBbox = true;
			}
		}
	}
	if (hasBbox) {
		return { bbox: [minLng, minLat, maxLng, maxLat] };
	}
	for (const area of areas) {
		const center = area.center;
		if (center && (center[0] !== 0 || center[1] !== 0)) {
			return { center: center as [number, number], zoom: 5 };
		}
	}
	return null;
};

export const getCenterOnTarget = (area: ServiceArea, zoom?: number): CenterOnTarget | null => {
	// Prefer `bbox` over a `center + zoom` heuristic so the full selected polygon fits in view
	// (e.g. Texas at zoom 7 only shows a corner of the state — fitBounds on Texas's actual bbox
	// frames the whole state instead). `bbox` is now the real polygon bbox after the search
	// resolver swaps in the unclipped source feature. `ServiceAreaMap` caps fitBounds with
	// `maxZoom: 8`, so small counties still get a comfortable zoom instead of over-zooming.
	if (
		area.bbox &&
		area.bbox.length === 4 &&
		!(area.bbox[0] === 0 && area.bbox[1] === 0 && area.bbox[2] === 0 && area.bbox[3] === 0)
	) {
		return { bbox: area.bbox as [number, number, number, number] };
	}
	if (area.center && (area.center[0] !== 0 || area.center[1] !== 0)) {
		return { center: area.center, zoom: zoom || 7 };
	}
	return null;
};
